import { StickyNote, Trash2, Edit2, Plus, FileText } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import type { Note, NavigationCallbacks, HighlightColor as HighlightColorType } from '../types';
import { HighlightColor, NoteType } from '../types';
import styles from '../ReaderPanels.module.css';
import { useAlert } from '../../../contexts/AlertContext';
import { useTranslation } from '../../../i18n/useTranslation';
import {
  primaryButtonStyle,
  secondaryButtonStyle,
  compactInputStyle,
  compactTextareaStyle,
  buttonRowStyle,
} from './controls';

interface NotesPanelProps {
  notes: Note[];
  currentLocation: string | number;
  currentLocationLabel: string;
  onNavigate: NavigationCallbacks['onNavigateToNote'];
  onDelete: (noteId: string) => Promise<void>;
  onUpdate: (noteId: string, noteText: string) => Promise<void>;
  onAddPageNote?: (noteText: string, color: HighlightColorType) => void;
  isOffline?: boolean; // NEW: Hide edit/delete buttons in offline mode
}

const HIGHLIGHT_COLOR_KEYS: Record<HighlightColor, string> = {
  [HighlightColor.Yellow]: 'App.Reader.Notes.Color.Yellow',
  [HighlightColor.Green]: 'App.Reader.Notes.Color.Green',
  [HighlightColor.Blue]: 'App.Reader.Notes.Color.Blue',
  [HighlightColor.Pink]: 'App.Reader.Notes.Color.Pink',
  [HighlightColor.Purple]: 'App.Reader.Notes.Color.Purple',
  [HighlightColor.Orange]: 'App.Reader.Notes.Color.Orange',
  [HighlightColor.Red]: 'App.Reader.Notes.Color.Red',
  [HighlightColor.Teal]: 'App.Reader.Notes.Color.Teal',
  [HighlightColor.Lime]: 'App.Reader.Notes.Color.Lime',
  [HighlightColor.Amber]: 'App.Reader.Notes.Color.Amber',
};

/**
 * NotesPanel
 *
 * Displays notes and highlights with color filtering.
 * Supports both text highlights and page-level notes.
 * Uses CSS modules for modern, accessible styling.
 */
export default function NotesPanel({
  notes,
  currentLocation,
  currentLocationLabel,
  onNavigate,
  onDelete,
  onUpdate,
  onAddPageNote,
  isOffline = false, // Default to false (online mode)
}: NotesPanelProps) {
  const { t } = useTranslation();
  const highlightColorLabels = Object.fromEntries(
    Object.entries(HIGHLIGHT_COLOR_KEYS).map(([k, key]) => [k, t(key)])
  ) as Record<HighlightColor, string>;
  const { showToast, confirm } = useAlert();

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [filterColor, setFilterColor] = useState<HighlightColor | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [isAddingPageNote, setIsAddingPageNote] = useState(false);
  const [pageNoteText, setPageNoteText] = useState('');
  const [pageNoteColor, setPageNoteColor] = useState<HighlightColorType>('#FFEB3B' as HighlightColorType);
  const addCardRef = useRef<HTMLDivElement>(null);
  const addTextareaRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    if (isAddingPageNote) {
      addCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      addTextareaRef.current?.focus();
    }
  }, [isAddingPageNote]);

  const handleEdit = (note: Note) => {
    setEditingId(note.id);
    setEditText(note.noteText || '');
  };

  const handleSaveEdit = async (noteId: string) => {
    try {
      await onUpdate(noteId, editText);
      setEditingId(null);
      setEditText('');
    } catch (error) {
      console.error('Failed to update note:', error);
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditText('');
  };

  const handleDelete = async (noteId: string) => {
    const ok = await confirm({
      title: t('App.Reader.Notes.DeleteConfirmTitle'),
      message: t('App.Reader.Notes.DeleteConfirmMessage'),
      confirmText: t('App.Reader.Notes.DeleteConfirmButton'),
      variant: 'danger',
    });
    if (ok) {
      try {
        await onDelete(noteId);
      } catch (error) {
        console.error('Failed to delete note:', error);
        showToast(t('App.Reader.Notes.DeleteError'), { type: 'error' });
      }
    }
  };

  const handleAddPageNote = () => {
    if (onAddPageNote && pageNoteText.trim()) {
      onAddPageNote(pageNoteText.trim(), pageNoteColor);
      setIsAddingPageNote(false);
      setPageNoteText('');
      setPageNoteColor('#FFEB3B' as HighlightColorType);
    }
  };

  const handleCancelAddPageNote = () => {
    setIsAddingPageNote(false);
    setPageNoteText('');
    setPageNoteColor('#FFEB3B' as HighlightColorType);
  };

  // Filter by color
  const colorFilteredNotes =
    filterColor === 'all'
      ? notes
      : notes.filter((note) => note.color === filterColor);

  // Filter by search query
  const searchFilteredNotes = searchQuery.trim()
    ? colorFilteredNotes.filter((note) => {
        const query = searchQuery.toLowerCase();
        return (
          note.selectedText?.toLowerCase().includes(query) ||
          note.noteText?.toLowerCase().includes(query) ||
          note.locationLabel.toLowerCase().includes(query)
        );
      })
    : colorFilteredNotes;

  // Sort by location
  const sortedNotes = [...searchFilteredNotes].sort((a, b) => {
    const locA = typeof a.location === 'number' ? a.location : 0;
    const locB = typeof b.location === 'number' ? b.location : 0;
    return locA - locB;
  });

  return (
    <div className={styles.panelContainer} style={{ width: '100%', padding: '20px' }}>
      {/* Header */}
      <div className={styles.panelHeader} style={{ padding: '0', paddingBottom: 'var(--spacing-3)', borderBottom: '1px solid var(--color-border)', margin: '0 -20px', paddingLeft: '20px', paddingRight: '20px' }}>
        {/* Search Input and Add Button */}
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('App.Reader.Notes.SearchPlaceholder')}
            style={{ ...compactInputStyle, flex: 1 }}
            aria-label={t('App.Reader.Notes.SearchPlaceholder')}
          />
          {onAddPageNote && !isOffline && ( // Hide Add button in offline mode
            <button
              onClick={() => setIsAddingPageNote(true)}
              style={{
                ...primaryButtonStyle,
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                flex: '0 0 auto',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgb(var(--color-primary-700))';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'rgb(var(--color-primary-600))';
              }}
              title={t('App.Reader.Notes.AddPageNote')}
              aria-label={t('App.Reader.Notes.AddPageNote')}
            >
              <Plus style={{ width: '16px', height: '16px' }} />
              <span>{t('App.Reader.Notes.Add')}</span>
            </button>
          )}
        </div>

        {/* Color Filter Buttons */}
        <div style={{ marginTop: 'var(--spacing-2)' }}>
          {/* All colors in single row with "All" as white circle */}
          <div style={{
            display: 'flex',
            gap: '0.5rem',
            flexWrap: 'wrap',
          }}>
            {/* All Filter - White circle */}
            <button
              onClick={() => setFilterColor('all')}
              style={{
                position: 'relative',
                width: '36px',
                height: '36px',
                backgroundColor: 'white',
                border: filterColor === 'all' ? '3px solid #1f2937' : '2px solid #e5e7eb',
                borderRadius: '50%',
                cursor: 'pointer',
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.75rem',
                fontWeight: '600',
                color: 'rgb(var(--color-neutral-700))',
                boxShadow: filterColor === 'all'
                  ? '0 0 0 3px rgb(var(--color-primary-500) / 0.3)'
                  : '0 1px 2px rgba(0, 0, 0, 0.1)',
                transform: filterColor === 'all' ? 'scale(1.05)' : 'scale(1)',
              }}
              aria-label={t('App.Reader.Notes.FilterAll', { count: notes.length })}
              title={t('App.Reader.Notes.FilterAll', { count: notes.length })}
              onMouseEnter={(e) => {
                if (filterColor !== 'all') {
                  e.currentTarget.style.transform = 'scale(1.1)';
                  e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.15)';
                }
              }}
              onMouseLeave={(e) => {
                if (filterColor !== 'all') {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.1)';
                }
              }}
            >
              {notes.length}
            </button>

            {/* Color Circles - 6 colors */}
            {Object.entries(HighlightColor).slice(0, 6).map(([key, color]) => {
              const count = notes.filter((n) => n.color === color).length;
              const isActive = filterColor === color;
              const hasNotes = count > 0;

              return (
                <button
                  key={color}
                  onClick={() => setFilterColor(color)}
                  style={{
                    position: 'relative',
                    width: '36px',
                    height: '36px',
                    backgroundColor: color,
                    border: isActive ? '3px solid #1f2937' : '2px solid #e5e7eb',
                    borderRadius: '50%',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    opacity: hasNotes ? 1 : 0.6,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    color: 'white',
                    textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
                    boxShadow: isActive
                      ? '0 0 0 3px rgb(var(--color-primary-500) / 0.3)'
                      : '0 1px 2px rgba(0, 0, 0, 0.1)',
                    transform: isActive ? 'scale(1.05)' : 'scale(1)',
                  }}
                  aria-label={t('App.Reader.Notes.FilterByColor', { color: highlightColorLabels[color], count })}
                  title={t('App.Reader.Notes.FilterByColor', { color: highlightColorLabels[color], count })}
                  onMouseEnter={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.transform = 'scale(1.1)';
                      e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.15)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.transform = 'scale(1)';
                      e.currentTarget.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.1)';
                    }
                  }}
                >
                  {count}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Notes List */}
      <div className={styles.panelBody} style={{ padding: '0', paddingTop: 'var(--spacing-3)', margin: '0 -20px', paddingLeft: '20px', paddingRight: '20px' }}>
        {/* Inline Add Page Note Card — mirrors the edit-mode layout */}
        {isAddingPageNote && (
          <div
            ref={addCardRef}
            className={styles.noteCard}
            style={{
              padding: 'var(--spacing-2)',
              borderLeft: `4px solid ${pageNoteColor}`,
              paddingLeft: 'var(--spacing-2)',
              margin: 0,
              marginBottom: 'var(--spacing-2)',
            }}
          >
            <div className={styles.noteCardHeader} style={{ gap: 'var(--spacing-2)' }}>
              <div style={{ flex: 1, textAlign: 'left', padding: 0 }}>
                <div
                  className={styles.searchResultLocation}
                  style={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    marginBottom: 'var(--spacing-1)',
                  }}
                >
                  {currentLocationLabel}
                </div>

                {/* Page-level note indicator */}
                <div
                  className={styles.noteCardHighlight}
                  style={{
                    borderLeftColor: pageNoteColor,
                    padding: 'var(--spacing-1) var(--spacing-2)',
                    marginTop: 'var(--spacing-1)',
                    fontSize: '0.875rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.375rem',
                    fontStyle: 'italic',
                    opacity: 0.8,
                  }}
                >
                  <FileText style={{ width: '14px', height: '14px' }} aria-hidden="true" />
                  {t('App.Reader.Notes.PageLevelNote')}
                </div>

                {/* Compact color picker row */}
                <div
                  style={{
                    display: 'flex',
                    gap: '0.375rem',
                    marginTop: 'var(--spacing-2)',
                    flexWrap: 'wrap',
                  }}
                  role="radiogroup"
                  aria-label={t('App.Reader.Notes.HighlightColorLabel')}
                >
                  {Object.entries(HighlightColor).slice(0, 6).map(([, color]) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setPageNoteColor(color)}
                      role="radio"
                      aria-checked={pageNoteColor === color}
                      aria-label={highlightColorLabels[color as HighlightColor]}
                      title={highlightColorLabels[color as HighlightColor]}
                      style={{
                        width: '22px',
                        height: '22px',
                        borderRadius: '50%',
                        backgroundColor: color,
                        border:
                          pageNoteColor === color
                            ? '2px solid #1f2937'
                            : '2px solid #e5e7eb',
                        cursor: 'pointer',
                        padding: 0,
                        transition: 'transform 0.15s',
                        transform: pageNoteColor === color ? 'scale(1.1)' : 'scale(1)',
                      }}
                    />
                  ))}
                </div>

                {/* Textarea + Save/Cancel (same layout as edit mode) */}
                <div style={{ marginTop: '6px' }}>
                  <textarea
                    ref={addTextareaRef}
                    value={pageNoteText}
                    onChange={(e) => setPageNoteText(e.target.value)}
                    rows={2}
                    placeholder={t('App.Reader.Notes.NotePlaceholder')}
                    aria-label={t('App.Reader.Notes.NewNoteAriaLabel')}
                    style={compactTextareaStyle}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') {
                        handleCancelAddPageNote();
                      }
                    }}
                  />
                  <div style={buttonRowStyle}>
                    <button
                      onClick={handleAddPageNote}
                      disabled={!pageNoteText.trim()}
                      aria-label={t('App.Reader.Notes.SaveAriaLabel')}
                      style={{
                        ...primaryButtonStyle,
                        opacity: pageNoteText.trim() ? 1 : 0.5,
                        cursor: pageNoteText.trim() ? 'pointer' : 'not-allowed',
                      }}
                    >
                      {t('App.Reader.Notes.Save')}
                    </button>
                    <button
                      onClick={handleCancelAddPageNote}
                      aria-label={t('App.Reader.Notes.CancelAddAriaLabel')}
                      style={secondaryButtonStyle}
                    >
                      {t('App.Reader.Notes.Cancel')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {sortedNotes.length === 0 && !isAddingPageNote && (
          <div className={styles.emptyState}>
            <StickyNote className={styles.emptyStateIcon} />
            <h3 className={styles.emptyStateTitle}>
              {filterColor === 'all'
                ? t('App.Reader.Notes.EmptyTitle')
                : t('App.Reader.Notes.EmptyColorTitle', { color: highlightColorLabels[filterColor] })}
            </h3>
            <p className={styles.emptyStateDescription}>
              {t('App.Reader.Notes.EmptyDescription')}
            </p>
          </div>
        )}

        {sortedNotes.map((note) => (
          <div
            key={note.id}
            className={styles.noteCard}
            style={{
              padding: 'var(--spacing-2)',
              borderLeft: `4px solid ${note.color}`,
              paddingLeft: 'var(--spacing-2)',
              margin: 0,
              marginBottom: 'var(--spacing-2)'
            }}
          >
            <div className={styles.noteCardHeader} style={{ gap: 'var(--spacing-2)' }}>
              <div
                style={{ flex: 1, textAlign: 'left', padding: 0 }}
              >
                <button
                  type="button"
                  className={styles.searchResultLocation + " text-left w-full"}
                  onClick={() => {
                    if (editingId !== note.id) {
                      console.log('Navigating to note:', note);
                      onNavigate(note);
                    }
                  }}
                  style={{
                    cursor: editingId !== note.id ? 'pointer' : 'default',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    marginBottom: 'var(--spacing-1)'
                  }}
                >
                  {note.locationLabel}
                </button>

                {/* Highlighted Text or Page Note Indicator */}
                {note.selectedText ? (
                  <button
                    type="button"
                    className={styles.noteCardHighlight + " text-left w-full"}
                    style={{
                      borderLeftColor: note.color,
                      padding: 'var(--spacing-1) var(--spacing-2)',
                      marginTop: 'var(--spacing-1)',
                      fontSize: '0.875rem',
                      lineHeight: '1.4'
                    }}
                    onClick={() => {
                      if (editingId !== note.id) {
                        console.log('Navigating to note (highlight text):', note);
                        onNavigate(note);
                      }
                    }}
                  >
                    "{note.selectedText}"
                  </button>
                ) : (
                  <button
                    type="button"
                    className={styles.noteCardHighlight + " text-left w-full"}
                    style={{
                      borderLeftColor: note.color,
                      padding: 'var(--spacing-1) var(--spacing-2)',
                      marginTop: 'var(--spacing-1)',
                      fontSize: '0.875rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.375rem',
                      fontStyle: 'italic',
                      opacity: 0.8
                    }}
                    onClick={() => {
                      if (editingId !== note.id) {
                        console.log('Navigating to note (page note):', note);
                        onNavigate(note);
                      }
                    }}
                  >
                    <FileText style={{ width: '14px', height: '14px' }} aria-hidden="true" />
                    {t('App.Reader.Notes.PageLevelNote')}
                  </button>
                )}

                {/* Note Text */}
                {editingId === note.id ? (
                  <div style={{ marginTop: '6px' }}>
                    <textarea
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      rows={2}
                      placeholder={t('App.Reader.Notes.NotePlaceholder')}
                      autoFocus
                      aria-label={t('App.Reader.Notes.EditAriaLabel')}
                      style={compactTextareaStyle}
                    />
                    <div style={buttonRowStyle}>
                      <button
                        onClick={() => handleSaveEdit(note.id)}
                        aria-label={t('App.Reader.Notes.SaveAriaLabel')}
                        style={primaryButtonStyle}
                      >
                        {t('App.Reader.Notes.Save')}
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        aria-label={t('App.Reader.Notes.Cancel')}
                        style={secondaryButtonStyle}
                      >
                        {t('App.Reader.Notes.Cancel')}
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    {note.noteText && (
                      <div className={styles.noteCardNote} style={{ marginTop: 'var(--spacing-1)', fontSize: '0.875rem', lineHeight: '1.4' }}>
                        {note.noteText}
                      </div>
                    )}
                    <div className={styles.noteCardMeta} style={{ marginTop: 'var(--spacing-1)', fontSize: '0.75rem' }}>
                      {new Date(note.createdAt).toLocaleDateString()}
                    </div>
                  </>
                )}
              </div>

              {/* Actions (hidden in offline mode) */}
              {editingId !== note.id && !isOffline && (
                <div className={styles.noteCardActions} style={{ gap: 'var(--spacing-1)', paddingLeft: 'var(--spacing-2)' }}>
                  <button
                    onClick={() => handleEdit(note)}
                    className={styles.noteCardActionButton}
                    aria-label={t('App.Reader.Notes.EditAriaLabel')}
                    title={t('App.Reader.Notes.EditTooltip')}
                    style={{ padding: '0.375rem', minWidth: '32px', minHeight: '32px' }}
                  >
                    <Edit2 style={{ width: '14px', height: '14px' }} />
                  </button>
                  <button
                    onClick={() => handleDelete(note.id)}
                    className={styles.noteCardActionButton}
                    aria-label={t('App.Reader.Notes.DeleteAriaLabel')}
                    title={t('App.Reader.Notes.DeleteAriaLabel')}
                    style={{ padding: '0.375rem', minWidth: '32px', minHeight: '32px' }}
                  >
                    <Trash2 style={{ width: '14px', height: '14px' }} />
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

    </div>
  );
}
